/**
 * Store Zustand pour la gestion des projets.
 */

import { create } from "zustand";
import {
  Parcel,
  Project,
  ProjectListItem,
  ProjectPhaseContext,
  ProjectPhase,
  TeamSubmission,
  ViewMode,
} from "@/types/project";
import type { ViewerLayerControlKey } from "@/types/project";
import {
  ProjectLoadCallbacks,
  ProjectManager,
} from "@/lib/cesium/ProjectManager";
import type { ModelLoadingProgress } from "@/lib/cesium/types";
import { useAuthStore } from "./authStore";

interface ProjectStoreState {
  projectPhases: ProjectPhase[];
  selectedPhaseId: string | null;
  phaseLoading: boolean;
  phaseError: string | null;
  fullProject: Project | null;
  projects: ProjectListItem[];
  projectsLoading: boolean;
  projectsInitialized: boolean;
  projectsError: string | null;

  currentProject: Project | null;
  currentProjectLoading: boolean;
  currentProjectError: string | null;

  currentParcel: Parcel | null;

  // Sélections indépendantes
  currentBuildingSubmission: TeamSubmission | null;
  currentLandscapeSubmission: TeamSubmission | null;
  isSubmissionLoading: boolean;
  buildingSubmissionError: string | null;
  landscapeSubmissionError: string | null;

  // Compatibilité temporaire avec l'existant
  currentSubmission: TeamSubmission | null;

  viewMode: ViewMode;

  basemapVisible: boolean;
  basemapOpacity: number;
  gabaritsLoaded: boolean;
  allowMultipleSubmissions: boolean;
  selectedSubmissionIds: string[];
  submissionErrors: Record<string, string>;
  gabaritHiddenByPhase: boolean;
  gabaritVisible: boolean;
  gabaritOpacity: number;
  architectureVisible: boolean;
  architectureOpacity: number;

  projectManager: ProjectManager | null;
}

interface ProjectStoreActions {
  selectPhase: (phaseId: string) => Promise<void>;
  retryPhases: () => Promise<void>;
  initialize: () => Promise<void>;
  dispose: () => Promise<void>;

  fetchProjects: () => Promise<void>;
  loadProject: (
    projectId: string,
    callbacks?: ProjectLoadCallbacks,
  ) => Promise<void>;
  updateCurrentProject: (project: Project) => void;
  unloadProject: () => Promise<void>;

  switchToSubmission: (
    submissionId: string,
    onModelLoadingProgress?: (progress: ModelLoadingProgress) => void,
  ) => Promise<boolean>;
  switchToTeamSubmission: (teamId: string) => Promise<void>;
  refreshCurrentParcelSubmissions: () => Promise<TeamSubmission[] | null>;
  switchToParcel: (parcelId: string) => Promise<void>;

  setViewMode: (mode: ViewMode) => void;
  showBefore: () => void;
  showAfter: () => void;
  showSolo: () => void;

  toggleBasemapVisibility: () => void;
  toggleAllGabarits: () => Promise<void>;
  toggleGabaritVisibility: () => void;
  toggleArchitectureVisibility: () => void;
  setLayerOpacity: (
    layerKey: ViewerLayerControlKey,
    opacity: number,
  ) => void;
  setBasemapOpacity: (opacity: number) => void;
  setGabaritOpacity: (opacity: number) => void;
  setArchitectureOpacity: (opacity: number) => void;

  toggleFavorite: (submissionId: string, parcelId: string) => Promise<void>;
  deleteSubmission: (submissionId: string, parcelId: string) => Promise<void>;
}

type ProjectStore = ProjectStoreState & ProjectStoreActions;

const API_BASE_URL = (import.meta.env.VITE_API_URL || "").replace(
  /\/api\/?$/,
  "",
);

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = useAuthStore.getState().token;

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });

  if (response.status === 401 || response.status === 403) {
    useAuthStore.getState().logout();
    if (!window.location.pathname.startsWith("/login")) {
      window.location.href = "/login";
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Erreur HTTP ${response.status}`);
  }

  return response.json();
}

const phaseSelections = new Map<string, string[]>();
function phaseSelectionKey(projectId: string, phaseId: string | null): string {
  return `${useAuthStore.getState().user?.id ?? ""}:${projectId}:${phaseId}`;
}
let phaseRequest = 0;

function projectForPhase(project: Project, phaseId: string | null): Project {
  return phaseId ? { ...project, parcels: project.parcels.map(parcel => ({
    ...parcel, submissions: parcel.submissions.filter(submission => submission.phaseId === phaseId),
  })) } : project;
}

/** Fusionne les données visibles sans perdre les soumissions des autres phases. */
function mergePhaseProject(full: Project, visible: Project, phaseId: string | null): Project {
  if (!phaseId) return visible;
  return { ...full, ...visible, parcels: visible.parcels.map(parcel => ({
    ...parcel, submissions: [
      ...(full.parcels.find(item => item.id === parcel.id)?.submissions ?? []).filter(
        submission => submission.phaseId !== phaseId && !parcel.submissions.some(item => item.id === submission.id),
      ),
      ...parcel.submissions,
    ],
  })) };
}

function updateSubmissionInProject(project: Project | null, id: string, update: (submission: TeamSubmission) => TeamSubmission | null): Project | null {
  return project ? { ...project, parcels: project.parcels.map(parcel => ({
    ...parcel, submissions: parcel.submissions.flatMap(submission => {
      if (submission.id !== id) return [submission];
      const updated = update(submission);
      return updated ? [updated] : [];
    }),
  })) } : null;
}

export const useProjectStore = create<ProjectStore>((set, get) => ({
  projectPhases: [], selectedPhaseId: null, phaseLoading: false, phaseError: null, fullProject: null,
  retryPhases: async () => {
    const project = get().fullProject;
    if (!get().currentProject) { window.location.reload(); return; }
    if (get().selectedPhaseId) await get().selectPhase(get().selectedPhaseId!);
    else if (project) await get().loadProject(project.id);
  },
  selectPhase: async (phaseId) => {
    const state = get();
    const phase = state.projectPhases.find(item => item.id === phaseId);
    if (!phase || !state.fullProject || state.phaseLoading || (phaseId === state.selectedPhaseId && !state.phaseError)) return;
    const request = ++phaseRequest;
    // Répercuter les favoris et suppressions de la phase quittée dans les données complètes.
    const fullProject = state.currentProject
      ? mergePhaseProject(state.fullProject, state.currentProject, state.selectedPhaseId)
      : state.fullProject;
    const project = projectForPhase(fullProject, phaseId);
    const key = phaseSelectionKey(project.id, phaseId);
    phaseSelections.set(phaseSelectionKey(project.id, state.selectedPhaseId), state.selectedSubmissionIds);
    set({ fullProject, selectedPhaseId: phaseId, phaseLoading: true, phaseError: null });
    try {
      await state.projectManager?.setConsultedPhase(project, phase, phaseSelections.get(key));
      if (request === phaseRequest) set({
        currentProject: project,
        currentParcel: project.parcels.find(parcel => parcel.id === state.currentParcel?.id) ?? project.parcels[0] ?? null,
        allowMultipleSubmissions: phase.allowMultipleSubmissions ?? false,
        gabaritHiddenByPhase: phase.hideGabarit ?? false,
      });
    } catch (error) {
      if (request === phaseRequest) set({ selectedPhaseId: state.selectedPhaseId, phaseError: error instanceof Error ? error.message : "Impossible de charger la phase" });
    } finally {
      if (request === phaseRequest) set({ phaseLoading: false });
    }
  },
  projects: [],
  projectsLoading: false,
  projectsInitialized: false,
  projectsError: null,

  currentProject: null,
  currentProjectLoading: false,
  currentProjectError: null,

  currentParcel: null,

  currentBuildingSubmission: null,
  currentLandscapeSubmission: null,
  isSubmissionLoading: false,
  buildingSubmissionError: null,
  landscapeSubmissionError: null,
  currentSubmission: null,

  viewMode: "after",

  basemapVisible: true,
  basemapOpacity: 1.0,
  gabaritsLoaded: false,
  allowMultipleSubmissions: false,
  selectedSubmissionIds: [],
  submissionErrors: {},
  gabaritHiddenByPhase: false,
  gabaritVisible: false,
  gabaritOpacity: 1.0,
  architectureVisible: true,
  architectureOpacity: 1.0,

  projectManager: null,

  initialize: async () => {
    const { projectManager } = get();

    if (projectManager) {
      await projectManager.dispose();
    }

    const manager = new ProjectManager();

    manager.onStateChange((state) => {
      const { projectManager: currentManager } = get();
      if (currentManager !== manager) {
        return;
      }

      set({
        currentProject: state.project,
        currentParcel: state.currentParcel,
        currentBuildingSubmission: state.currentBuildingSubmission,
        currentLandscapeSubmission: state.currentLandscapeSubmission,
        isSubmissionLoading: state.isSubmissionLoading,
        buildingSubmissionError: state.buildingSubmissionError,
        landscapeSubmissionError: state.landscapeSubmissionError,
        currentSubmission:
          state.currentBuildingSubmission ?? state.currentLandscapeSubmission,
        viewMode: state.viewMode,
        basemapVisible: state.basemapVisible,
        basemapOpacity: state.basemapOpacity,
        gabaritsLoaded: state.gabaritsLoaded,
        allowMultipleSubmissions: state.allowMultipleSubmissions ?? false,
        selectedSubmissionIds: state.selectedSubmissionIds ?? [],
        submissionErrors: state.submissionErrors ?? {},
        gabaritHiddenByPhase: state.gabaritHiddenByPhase ?? false,
        gabaritVisible: state.gabaritVisible,
        gabaritOpacity: state.gabaritOpacity,
        architectureVisible: state.architectureVisible,
        architectureOpacity: state.architectureOpacity,
      });
    });

    set({ projectManager: manager });
  },

  dispose: async () => {
    ++phaseRequest;
    const managerToDispose = get().projectManager;

    if (managerToDispose) {
      await managerToDispose.dispose();
    }

    set((state) => {
      if (state.projectManager !== managerToDispose) {
        return state;
      }

      return {
        ...state,
        projectManager: null,
        projectPhases: [], selectedPhaseId: null, fullProject: null, phaseLoading: false, phaseError: null,
        currentProject: null,
        currentParcel: null,
        currentBuildingSubmission: null,
        currentLandscapeSubmission: null,
        currentSubmission: null,
        isSubmissionLoading: false,
        buildingSubmissionError: null,
        landscapeSubmissionError: null,
        basemapOpacity: 1.0,
        gabaritsLoaded: false,
        allowMultipleSubmissions: false,
        selectedSubmissionIds: [],
        submissionErrors: {},
        gabaritHiddenByPhase: false,
        gabaritVisible: false,
        gabaritOpacity: 1.0,
        architectureOpacity: 1.0,
      };
    });
  },

  fetchProjects: async () => {
    set({ projectsLoading: true, projectsError: null });

    try {
      const response = await fetchApi<{ success: boolean; data: ProjectListItem[] }>(
        "/api/projects",
      );

      if (response.success && response.data) {
        set({ projects: response.data });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erreur inconnue";
      set({ projectsError: message });
      console.error("[ProjectStore] Erreur lors du chargement des projets:", error);
    } finally {
      set({ projectsLoading: false, projectsInitialized: true });
    }
  },

  loadProject: async (
    projectId: string,
    callbacks?: ProjectLoadCallbacks,
  ) => {
    const { projectManager } = get();
    const request = ++phaseRequest;
    set({ projectPhases: [], selectedPhaseId: null, currentProjectLoading: true, currentProjectError: null, phaseLoading: true, phaseError: null });

    try {
      const isReviewer = ["CLIENT", "ADMIN"].includes(useAuthStore.getState().user?.role ?? "");
      const [response, context, phasesResponse] = await Promise.all([
        fetchApi<{ success: boolean; data: Project }>(`/api/projects/${projectId}`),
        fetchApi<{ success: boolean; data: ProjectPhaseContext }>(`/api/projects/${projectId}/phase-context`),
        isReviewer ? fetchApi<{ success: boolean; data: ProjectPhase[] }>(`/api/projects/${projectId}/phases`).catch(error => { if (request === phaseRequest) set({ phaseError: error instanceof Error ? error.message : "Chargement des phases impossible" }); return { success: false, data: [] as ProjectPhase[] }; }) : Promise.resolve({ success: true, data: [] as ProjectPhase[] }),
      ]);

      if (request !== phaseRequest) return;
      if (response.success && response.data) {
        set({ fullProject: response.data });
        if (!phasesResponse.success) throw new Error(get().phaseError ?? "Chargement des phases impossible");
        const phases = phasesResponse.data.sort((a, b) => a.order - b.order);
        const phase = phases.find(item => item.id === context.data.phase?.id)
          ?? [...phases].filter(item => Date.parse(item.uploadOpenAt) <= Date.now()).sort((a, b) => Date.parse(b.uploadOpenAt) - Date.parse(a.uploadOpenAt))[0]
          ?? phases[0];
        const behavior = phase ?? context.data.phase;
        const project = projectForPhase(response.data, phase?.id ?? null);
        set({ fullProject: response.data, projectPhases: phases, selectedPhaseId: phase?.id ?? null });
        if (projectManager) {
          await projectManager.loadProject(project, { ...callbacks, selectFirstSubmissions: Boolean(phase), hideGabarit: behavior?.hideGabarit ?? false, allowMultipleSubmissions: behavior?.allowMultipleSubmissions ?? false });
        } else {
          set({ currentProject: project, currentParcel: project.parcels[0] ?? null, allowMultipleSubmissions: behavior?.allowMultipleSubmissions ?? false });
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erreur inconnue";
      if (request !== phaseRequest) return;
      set({ currentProjectError: message, phaseError: message });
      console.error("[ProjectStore] Erreur lors du chargement du projet:", error);
    } finally {
      if (request === phaseRequest) set({ currentProjectLoading: false, phaseLoading: false });
    }
  },

  unloadProject: async () => {
    const { projectManager } = get();
    if (projectManager) {
      await projectManager.unloadProject();
    }
  },

  updateCurrentProject: (project) => {
    const { projectManager, projects } = get();

    const fullProject = get().fullProject;
    const mergedProject = fullProject ? mergePhaseProject(fullProject, project, get().selectedPhaseId) : project;
    const visibleProject = projectForPhase(mergedProject, get().selectedPhaseId);
    projectManager?.updateCurrentProject(visibleProject);

    set({
      fullProject: mergedProject,
      currentProject: visibleProject,
      projects: projects.map((item) =>
        item.id === project.id
          ? {
              ...item,
              name: project.name,
              description: project.description,
              defaultLatitude: project.defaultLatitude,
              defaultLongitude: project.defaultLongitude,
              defaultAltitude: project.defaultAltitude,
              defaultPitch: project.defaultPitch,
              overlayLayers: project.overlayLayers,
              updatedAt: project.updatedAt,
            }
          : item,
      ),
    });
  },

  switchToSubmission: async (submissionId: string, onModelLoadingProgress) => {
    const { projectManager } = get();
    if (projectManager) {
      const result = await projectManager.switchToSubmission(
        submissionId,
        onModelLoadingProgress,
      );
      return result.success;
    }
    return false;
  },

  switchToTeamSubmission: async (teamId: string) => {
    const { projectManager } = get();
    if (projectManager) {
      await projectManager.switchToTeamSubmission(teamId);
    }
  },

  switchToParcel: async (parcelId: string) => {
    const { projectManager } = get();
    if (projectManager) {
      await projectManager.switchToParcel(parcelId);
    } else {
      const parcel = get().currentProject?.parcels.find(item => item.id === parcelId);
      if (parcel) set({ currentParcel: parcel });
    }
  },

  refreshCurrentParcelSubmissions: async () => {
    const { currentProject, currentParcel, projectManager } = get();
    if (!currentProject || !currentParcel || !projectManager) {
      return null;
    }

    try {
      const response = await fetchApi<{ success: boolean; data: TeamSubmission[] }>(
        `/api/projects/${currentProject.id}/parcels/${currentParcel.id}/submissions${get().selectedPhaseId ? `?phaseId=${encodeURIComponent(get().selectedPhaseId!)}` : ""}`,
      );

      if (response.success) {
        projectManager.updateCurrentParcelSubmissions(response.data);
        return response.data;
      }
    } catch (error) {
      console.error("[ProjectStore] Erreur refresh soumissions:", error);
    }

    return null;
  },

  setViewMode: (mode: ViewMode) => {
    const { projectManager } = get();
    if (projectManager) {
      projectManager.setViewMode(mode);
    }
  },

  showBefore: () => get().setViewMode("before"),
  showAfter: () => get().setViewMode("after"),
  showSolo: () => get().setViewMode("solo"),

  toggleBasemapVisibility: () => {
    const { projectManager } = get();
    if (projectManager) {
      projectManager.toggleBasemapVisibility();
    }
  },

  toggleAllGabarits: async () => {
    const { projectManager } = get();
    if (projectManager) {
      await projectManager.toggleAllGabarits();
    }
  },

  toggleGabaritVisibility: () => {
    const { projectManager } = get();
    if (projectManager) {
      projectManager.toggleGabaritVisibility();
    }
  },

  toggleArchitectureVisibility: () => {
    const { projectManager } = get();
    if (projectManager) {
      projectManager.toggleArchitectureVisibility();
    }
  },

  setLayerOpacity: (layerKey, opacity) => {
    const { projectManager } = get();
    if (projectManager) {
      projectManager.setLayerOpacity(layerKey, opacity);
    }
  },

  setBasemapOpacity: (opacity: number) => {
    get().setLayerOpacity("basemap", opacity);
  },

  setGabaritOpacity: (opacity: number) => {
    get().setLayerOpacity("gabarit", opacity);
  },

  setArchitectureOpacity: (opacity: number) => {
    get().setLayerOpacity("project", opacity);
  },

  toggleFavorite: async (submissionId: string, parcelId: string) => {
    const { currentProject } = get();
    if (!currentProject) return;

    try {
      const response = await fetchApi<{
        success: boolean;
        data: { isFavorite: boolean };
      }>(
        `/api/projects/${currentProject.id}/parcels/${parcelId}/submissions/${submissionId}/favorite`,
        { method: "POST" },
      );

      if (response.success) {
        if (get().currentProject?.id !== currentProject.id) return;
        const updateFavorite = (submission: TeamSubmission) => ({ ...submission, isFavorite: response.data.isFavorite });
        set((state) => ({
          fullProject: updateSubmissionInProject(state.fullProject, submissionId, updateFavorite),
          currentProject: updateSubmissionInProject(state.currentProject, submissionId, updateFavorite),
          currentBuildingSubmission:
            state.currentBuildingSubmission?.id === submissionId
              ? {
                  ...state.currentBuildingSubmission,
                  isFavorite: response.data.isFavorite,
                }
              : state.currentBuildingSubmission,
          currentLandscapeSubmission:
            state.currentLandscapeSubmission?.id === submissionId
              ? {
                  ...state.currentLandscapeSubmission,
                  isFavorite: response.data.isFavorite,
                }
              : state.currentLandscapeSubmission,
          currentSubmission:
            state.currentSubmission?.id === submissionId
              ? {
                  ...state.currentSubmission,
                  isFavorite: response.data.isFavorite,
                }
              : state.currentSubmission,
        }));
        const updatedProject = get().currentProject;
        if (updatedProject) get().projectManager?.updateCurrentProject(updatedProject);
      }
    } catch (error) {
      console.error("[ProjectStore] Erreur lors du toggle favori:", error);
    }
  },

  deleteSubmission: async (submissionId: string, parcelId: string) => {
    const { currentProject, projectManager } = get();
    if (!currentProject || !projectManager) {
      return;
    }

    try {
      const response = await fetchApi<{ success: boolean }>(
        `/api/projects/${currentProject.id}/parcels/${parcelId}/submissions/${submissionId}`,
        { method: "DELETE" },
      );

      if (!response.success) {
        return;
      }

      if (get().currentProject?.id !== currentProject.id) return;
      set(state => ({ fullProject: updateSubmissionInProject(state.fullProject, submissionId, () => null) }));
      await projectManager.removeSubmissionFromParcel(parcelId, submissionId);
      if (get().currentProject?.id !== currentProject.id) return;

      set((state) => ({
        currentProject: state.currentProject
          ? {
              ...state.currentProject,
              parcels: state.currentProject.parcels.map((item) =>
                item.id === parcelId
                  ? {
                      ...item,
                      submissions: item.submissions.filter(
                        (submission) => submission.id !== submissionId,
                      ),
                    }
                  : item,
              ),
            }
          : null,
        currentSubmission:
          state.currentSubmission?.id === submissionId
            ? null
            : state.currentSubmission,
      }));
    } catch (error) {
      console.error("[ProjectStore] Erreur lors de la suppression:", error);
      throw error;
    }
  },
}));
